import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
  ArrowLeft,
  ArrowUpFromLine,
  BarChart3,
  Bike,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  KeyRound,
  Receipt,
  Settings2,
  SlidersHorizontal,
  Wallet,
} from "lucide-react";
import { Button, Modal, Pill, Select } from "@veorest/ui";
import { formatCurrency } from "@veorest/utils";
import { ROUTES, driverIdFromPath } from "../routes";
import { SegmentStrip } from "../components/data/SegmentStrip";
import { StatsStrip, type StatTileItem } from "../components/data/StatsStrip";
import { DriverModeField } from "../components/drivers/DriverModeField";
import {
  driverModeLabelKey,
  driverPresenceTranslationKey,
  driverVehicleTypeTranslationKey,
  mapDriversRpcError,
  partnerDriverPresence,
  vehicleLabel,
  type PartnerDriverMode,
  type PartnerDriverRow,
} from "../lib/partnerDrivers";
import {
  useActivatePartnerDriver,
  useDeactivatePartnerDriver,
  usePartnerDriversList,
  useReleasePartnerDriver,
  useResetPartnerDriverPassword,
  useSetPartnerDriverMode,
  useSetPartnerDriverTripFee,
  useUpdatePartnerDriver,
} from "../hooks/usePartnerDrivers";
import {
  usePartnerDriverWalletV2,
  useTopupPartnerDriverWalletV2,
} from "../hooks/usePartnerWallets";
import { usePartnerDriverOrders } from "../hooks/usePartnerOrders";
import { partnerStageTranslationKey } from "../lib/partnerOrders";
import { mapWalletV2Error, walletTxDescriptionKey, walletTxTypeKey } from "../lib/partnerWallets";
import { usePartnerProfile } from "../hooks/usePartnerSettings";
import { usePartnerSessionStore } from "../lib/partnerAuth";
import { formatShortDateTime } from "../lib/partnerReports";
import { mapAccountError, type AccountCredentials } from "../lib/partnerAccounts";
import { CredentialsModal } from "../components/accounts/CredentialsModal";
import { ResetPasswordModal } from "../components/accounts/ResetPasswordModal";
import { useI18n } from "../i18n";

type DriverTab = "settings" | "reports" | "wallet" | "operations";

/* ── Driver settings ────────────────────────────────────────── */

function DriverSettingsSection({
  driver,
  canManage,
}: {
  driver: PartnerDriverRow;
  canManage: boolean;
}) {
  const { t } = useI18n();
  const updateMutation = useUpdatePartnerDriver();
  const modeMutation = useSetPartnerDriverMode();
  const [name, setName] = useState(driver.name);
  const [phone, setPhone] = useState(driver.phone ?? "");
  const [vehicleType, setVehicleType] = useState<
    "bicycle" | "motorcycle" | "car"
  >(
    driver.vehicle_type === "car"
      ? "car"
      : driver.vehicle_type === "bicycle"
        ? "bicycle"
        : "motorcycle"
  );
  const [vehicleNumber, setVehicleNumber] = useState(
    driver.vehicle_number ?? ""
  );
  const [vehicleModel, setVehicleModel] = useState(driver.vehicle_model ?? "");
  const [vehicleColor, setVehicleColor] = useState(driver.vehicle_color ?? "");
  const [vehicleYear, setVehicleYear] = useState(
    driver.vehicle_year != null ? String(driver.vehicle_year) : ""
  );
  const [driverMode, setDriverMode] = useState<PartnerDriverMode>(
    driver.driver_mode ?? "paid"
  );

  useEffect(() => {
    setName(driver.name);
    setPhone(driver.phone ?? "");
    setVehicleType(
      driver.vehicle_type === "car"
        ? "car"
        : driver.vehicle_type === "bicycle"
          ? "bicycle"
          : "motorcycle"
    );
    setVehicleNumber(driver.vehicle_number ?? "");
    setVehicleModel(driver.vehicle_model ?? "");
    setVehicleColor(driver.vehicle_color ?? "");
    setVehicleYear(
      driver.vehicle_year != null ? String(driver.vehicle_year) : ""
    );
    setDriverMode(driver.driver_mode ?? "paid");
  }, [
    driver.id,
    driver.name,
    driver.phone,
    driver.vehicle_type,
    driver.vehicle_number,
    driver.vehicle_model,
    driver.vehicle_color,
    driver.vehicle_year,
    driver.driver_mode,
  ]);

  const yearParsed = vehicleYear.trim() === "" ? null : Number(vehicleYear);
  const yearValid =
    yearParsed == null ||
    (Number.isFinite(yearParsed) && yearParsed >= 1980 && yearParsed <= 2100);
  const dirty =
    driverMode !== (driver.driver_mode ?? "paid") ||
    name.trim() !== driver.name ||
    phone.trim() !== (driver.phone ?? "") ||
    vehicleType !== (driver.vehicle_type ?? "motorcycle") ||
    vehicleNumber.trim() !== (driver.vehicle_number ?? "") ||
    vehicleModel.trim() !== (driver.vehicle_model ?? "") ||
    vehicleColor.trim() !== (driver.vehicle_color ?? "") ||
    yearParsed !== (driver.vehicle_year ?? null);

  const handleSave = async () => {
    if (!yearValid) {
      toast.error(t("drivers.invalidYear"));
      return;
    }
    try {
      await updateMutation.mutateAsync({
        driverId: driver.id,
        input: {
          name: name.trim(),
          phone: phone.trim(),
          vehicleType,
          vehicleNumber: vehicleNumber.trim() || null,
          vehicleModel: vehicleModel.trim() || null,
          vehicleColor: vehicleColor.trim() || null,
          vehicleYear: yearParsed,
        },
      });
      if (driverMode !== (driver.driver_mode ?? "paid")) {
        await modeMutation.mutateAsync({ driverId: driver.id, mode: driverMode });
      }
      toast.success(t("drivers.toast.updated"));
    } catch (err) {
      toast.error(t(mapDriversRpcError(err)));
    }
  };

  const vehicleTypeOptions = (["motorcycle", "car", "bicycle"] as const).map(
    (vt) => ({
      value: vt,
      label: t(driverVehicleTypeTranslationKey(vt) ?? "common.dash"),
    })
  );

  const documentTile = (label: string, url: string | null) => (
    <div className="rounded-xl border border-[var(--border-subtle)] bg-[var(--surface-muted)] px-3 py-3">
      <p className="text-xs font-semibold text-[var(--text-muted)]">{label}</p>
      {url ? (
        <a
          className="mt-1 inline-flex items-center gap-1.5 text-sm font-bold text-[var(--accent)]"
          href={url}
          target="_blank"
          rel="noreferrer"
        >
          <ExternalLink className="h-4 w-4" />
          {t("drivers.open")}
        </a>
      ) : (
        <p className="mt-1 text-sm font-semibold text-[var(--text-muted)]">
          {t("common.dash")}
        </p>
      )}
    </div>
  );

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <section className="menu-panel">
        <div className="menu-panel__body space-y-4">
          <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
            {t("drivers.tabs.settings")}
          </h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <DriverModeField
                value={driverMode}
                onChange={setDriverMode}
                disabled={!canManage}
              />
            </div>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("drivers.name")}
              </span>
              <input
                className="input-base w-full"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={!canManage}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("drivers.phone")}
              </span>
              <input
                className="input-base w-full"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                dir="ltr"
                disabled={!canManage}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("drivers.vehicleType")}
              </span>
              <Select
                value={vehicleType}
                onChange={(e) =>
                  setVehicleType(
                    e.target.value as "bicycle" | "motorcycle" | "car"
                  )
                }
                options={vehicleTypeOptions}
                disabled={!canManage}
                aria-label={t("drivers.vehicleType")}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("drivers.vehicleNumber")}
              </span>
              <input
                className="input-base w-full"
                value={vehicleNumber}
                onChange={(e) => setVehicleNumber(e.target.value)}
                disabled={!canManage}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("drivers.vehicleModel")}
              </span>
              <input
                className="input-base w-full"
                value={vehicleModel}
                onChange={(e) => setVehicleModel(e.target.value)}
                disabled={!canManage}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("drivers.vehicleColor")}
              </span>
              <input
                className="input-base w-full"
                value={vehicleColor}
                onChange={(e) => setVehicleColor(e.target.value)}
                disabled={!canManage}
              />
            </label>
            <label className="block space-y-1.5">
              <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                {t("drivers.vehicleYear")}
              </span>
              <input
                className="input-base w-full"
                type="number"
                min="1980"
                max="2100"
                inputMode="numeric"
                value={vehicleYear}
                onChange={(e) => setVehicleYear(e.target.value)}
                disabled={!canManage}
              />
            </label>
          </div>
          {canManage ? (
            <div className="flex items-center gap-3 border-t border-[var(--border-subtle)] pt-4">
              <Button
                variant="primary"
                className="min-h-11 gap-2 px-5"
                disabled={
                  !dirty ||
                  !yearValid ||
                  updateMutation.isPending ||
                  modeMutation.isPending
                }
                onClick={() => void handleSave()}
              >
                {updateMutation.isPending || modeMutation.isPending
                  ? t("common.loading")
                  : t("common.save")}
              </Button>
            </div>
          ) : (
            <p className="text-[11px] text-[var(--text-muted)]">
              {t("drivers.editNotAvailable")}
            </p>
          )}
        </div>
      </section>

      <section className="menu-panel">
        <div className="menu-panel__body space-y-3">
          <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
            {t("drivers.documents")}
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            {documentTile(t("drivers.licenseImage"), driver.license_image_url)}
            {documentTile(t("drivers.selfPhoto"), driver.photo_url)}
          </div>
          <p className="text-[11px] text-[var(--text-muted)]">
            {t("drivers.missingFields")}
          </p>
        </div>
      </section>
    </div>
  );
}

/* ── Reports ────────────────────────────────────────────────── */

const REPORT_PAGE_SIZE = 10;

function DriverReportsSection({ driver }: { driver: PartnerDriverRow }) {
  const { t, locale } = useI18n();
  const [page, setPage] = useState(0);
  const ordersQuery = usePartnerDriverOrders(driver.id, {
    page,
    pageSize: REPORT_PAGE_SIZE,
  });

  const total = ordersQuery.data?.total ?? 0;
  const rows = ordersQuery.data?.orders ?? [];
  const pageCount = Math.max(1, Math.ceil(total / REPORT_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const from = total === 0 ? 0 : safePage * REPORT_PAGE_SIZE + 1;
  const to = Math.min((safePage + 1) * REPORT_PAGE_SIZE, total);
  const money = (v: number | null, currency: string) =>
    v == null ? "—" : formatCurrency(v, currency || "IQD", locale);

  return (
    <section className="menu-panel">
      <div className="menu-panel__body">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
            {t("drivers.reports.ordersHistory")}
          </h3>
        </div>
        <div className="report-table-wrap">
          <table className="table-flat">
            <thead>
              <tr>
                <th>{t("clientOrders.colDate")}</th>
                <th>{t("drivers.reports.orderNo")}</th>
                <th>{t("drivers.reports.client")}</th>
                <th>{t("drivers.columns.status")}</th>
                <th className="text-end">{t("orders.detail.total")}</th>
                <th className="text-end">{t("walletV2.colTripEarnings")}</th>
                <th className="text-end">{t("walletV2.colTripCash")}</th>
              </tr>
            </thead>
            <tbody>
              {ordersQuery.isLoading ? (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-sm text-[var(--text-muted)]"
                  >
                    {t("common.loading")}
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={7}
                    className="py-10 text-center text-sm text-[var(--text-muted)]"
                  >
                    {t("drivers.reports.noOrders")}
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id}>
                    <td className="whitespace-nowrap text-sm text-[var(--text-secondary)]">
                      {formatShortDateTime(row.created_at)}
                    </td>
                    <td className="whitespace-nowrap font-mono text-xs">
                      {row.displayNumber}
                    </td>
                    <td className="font-semibold text-[var(--text-primary)]">
                      {row.tenant_name}
                    </td>
                    <td>
                      <span className="text-xs font-bold text-[var(--accent)]">
                        {t(partnerStageTranslationKey(row.stage))}
                      </span>
                    </td>
                    <td className="text-end tabular">
                      {money(row.total, row.currency)}
                    </td>
                    <td className="text-end tabular">
                      {money(row.driver_earnings, row.currency)}
                    </td>
                    <td className="text-end tabular">
                      {row.cash_collected != null && row.cash_collected > 0
                        ? money(row.cash_collected, row.currency)
                        : "—"}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
          <p className="text-xs text-[var(--text-muted)]">
            {t("clientOrders.showing", { from, to, total })}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              disabled={safePage === 0 || ordersQuery.isLoading}
              className="min-h-9 gap-1 px-3"
            >
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
              {t("clientOrders.prev")}
            </Button>
            <span className="text-xs font-bold text-[var(--text-secondary)]">
              {safePage + 1} / {pageCount}
            </span>
            <Button
              variant="secondary"
              onClick={() => setPage((p) => (to < total ? p + 1 : p))}
              disabled={to >= total || ordersQuery.isLoading}
              className="min-h-9 gap-1 px-3"
            >
              {t("clientOrders.next")}
              <ChevronRight className="h-4 w-4 rtl:rotate-180" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ── Wallet ─────────────────────────────────────────────────── */

function DriverWalletSection({
  driver,
  canManage,
}: {
  driver: PartnerDriverRow;
  canManage: boolean;
}) {
  const { t, locale } = useI18n();
  const walletQuery = usePartnerDriverWalletV2(driver.id);
  const topupMutation = useTopupPartnerDriverWalletV2();
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [txPage, setTxPage] = useState(0);

  const wallet = walletQuery.data?.wallet;
  const breakdown = walletQuery.data?.breakdown;
  const balance = wallet?.balance ?? 0;
  const currency = wallet?.currency || "IQD";
  const money = (v: number) => {
    const abs = formatCurrency(Math.abs(v), currency, locale);
    return v < 0 ? `−${abs}` : abs;
  };
  const txns = walletQuery.data?.transactions ?? [];
  const TX_PAGE_SIZE = 10;
  const txPageCount = Math.max(1, Math.ceil(txns.length / TX_PAGE_SIZE));
  const txSafePage = Math.min(txPage, txPageCount - 1);
  const txRows = txns.slice(
    txSafePage * TX_PAGE_SIZE,
    txSafePage * TX_PAGE_SIZE + TX_PAGE_SIZE
  );
  const txFrom = txns.length === 0 ? 0 : txSafePage * TX_PAGE_SIZE + 1;
  const txTo = Math.min((txSafePage + 1) * TX_PAGE_SIZE, txns.length);

  // 249: the only manual money movement is ADD BALANCE (top-up the advance).
  const previewValue = Number(amount);
  const previewValid = Number.isFinite(previewValue) && previewValue > 0;
  const balanceAfter = balance + (previewValid ? previewValue : 0);

  const handleAddBalance = async () => {
    const value = Number(amount);
    if (!Number.isFinite(value) || value <= 0) {
      toast.error(t("errors.amountInvalid"));
      return;
    }
    try {
      await topupMutation.mutateAsync({
        driverId: driver.id,
        amount: value,
        note,
      });
      setAmount("");
      setNote("");
      toast.success(t("walletV2.toastCredited", { amount: money(value) }));
    } catch (err) {
      toast.error(t(mapWalletV2Error(err)));
    }
  };

  const tiles: StatTileItem[] = [
    {
      key: "trips",
      label: t("walletV2.bdTrips"),
      value: breakdown?.trips ?? 0,
      icon: Bike,
      tone: "info",
      caption: t("walletV2.stTripsCaption"),
    },
    {
      key: "income",
      label: t("walletV2.bdIncome"),
      value: breakdown ? money(breakdown.driver_income ?? 0) : "—",
      icon: Receipt,
      tone: "accent",
      caption: t("walletV2.stIncomeCaption"),
    },
    {
      key: "balance",
      label: t("walletV2.stWalletBalance"),
      value: wallet ? money(balance) : "—",
      icon: Wallet,
      tone: balance > 0 ? "accent" : balance < 0 ? "info" : "success",
      caption: t(
        balance < 0 ? "walletV2.stBalanceNegative" : "walletV2.stBalanceCaption"
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <StatsStrip tiles={tiles} loading={walletQuery.isLoading} />

      {canManage ? (
        <section className="menu-panel">
          <div className="menu-panel__body space-y-4">
            <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
              {t("walletV2.addBalanceTitle")}
            </h3>
            <span className="block text-[11px] font-semibold text-[var(--text-muted)]">
              {t("walletV2.addBalanceHint")}
            </span>
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("walletV2.settleAmount")}
                </span>
                <input
                  className="input-base w-full"
                  type="number"
                  step="any"
                  min="0"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </div>
              <label className="block space-y-1.5">
                <span className="text-xs font-bold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("walletV2.settleNote")}
                </span>
                <input
                  className="input-base w-full"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
            </div>

            {/* preview of the add, then confirm */}
            <div
              className="rounded-xl border px-3.5 py-3"
              style={{
                borderColor: "color-mix(in srgb, var(--accent) 35%, transparent)",
                background: "var(--accent-light)",
              }}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="inline-flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-wide text-[var(--text-secondary)]">
                  <ArrowUpFromLine
                    className="h-3.5 w-3.5"
                    strokeWidth={2.25}
                    aria-hidden
                  />
                  {t("walletV2.addBalanceTitle")}
                </span>
                <span className="tabular text-xl font-black text-[var(--text-primary)]">
                  {previewValid ? money(previewValue) : "—"}
                </span>
              </div>
              <p className="mt-1 text-[11px] font-semibold text-[var(--text-secondary)]">
                {previewValid
                  ? `${t("walletV2.addBalanceCurrent", {
                      amount: money(balance),
                    })} · ${t("walletV2.addBalanceAfter", {
                      amount: money(balanceAfter),
                    })}`
                  : t("walletV2.movePreviewHint")}
              </p>
              {previewValid && balanceAfter < 0 ? (
                <p className="mt-1 text-[11px] font-semibold text-[var(--text-secondary)]">
                  {t("walletV2.addBalanceNegative")}
                </p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant="primary"
                className="min-h-11 px-5"
                disabled={topupMutation.isPending}
                onClick={() => void handleAddBalance()}
              >
                {topupMutation.isPending
                  ? t("common.loading")
                  : t("walletV2.addBalanceSubmit")}
              </Button>
            </div>
          </div>
        </section>
      ) : null}

      <section className="menu-panel">
        <div className="menu-panel__body">
          <h3 className="mb-3 text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
            {t("walletV2.transactionsTitle")}
          </h3>
          {walletQuery.isLoading ? (
            <div className="h-16 rounded-xl skeleton" />
          ) : txns.length === 0 ? (
            <p className="text-sm text-[var(--text-muted)]">
              {t("walletV2.empty")}
            </p>
          ) : (
            <div className="report-table-wrap">
              <table className="table-flat">
                <thead>
                  <tr>
                    <th>{t("walletV2.colDate")}</th>
                    <th>{t("walletV2.colType")}</th>
                    <th>{t("walletV2.colDescription")}</th>
                    <th className="text-end">{t("walletV2.colAmount")}</th>
                  </tr>
                </thead>
                <tbody>
                  {txRows.map((tx) => {
                    const descKey = walletTxDescriptionKey(tx.description);
                    return (
                    <tr key={tx.id}>
                      <td className="whitespace-nowrap text-sm text-[var(--text-secondary)]">
                        {formatShortDateTime(tx.created_at)}
                      </td>
                      <td className="text-sm font-semibold text-[var(--text-primary)]">
                        {t(walletTxTypeKey(tx.type, tx.description))}
                      </td>
                      <td className="text-xs text-[var(--text-muted)]">
                        {descKey ? t(descKey) : tx.description || "—"}
                      </td>
                      <td
                        className={`text-end tabular font-bold ${
                          tx.amount < 0 ? "text-red-600" : "text-[var(--accent)]"
                        }`}
                      >
                        {money(tx.amount)}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {txPageCount > 1 ? (
            <div className="mt-3 flex flex-wrap items-center justify-between gap-2">
              <p className="text-xs text-[var(--text-muted)]">
                {t("clientOrders.showing", {
                  from: txFrom,
                  to: txTo,
                  total: txns.length,
                })}
              </p>
              <div className="flex items-center gap-2">
                <Button
                  variant="secondary"
                  onClick={() => setTxPage((p) => Math.max(0, p - 1))}
                  disabled={txSafePage === 0}
                  className="min-h-8 gap-1 px-3"
                >
                  <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
                  {t("clientOrders.prev")}
                </Button>
                <span className="text-xs font-bold text-[var(--text-secondary)]">
                  {txSafePage + 1} / {txPageCount}
                </span>
                <Button
                  variant="secondary"
                  onClick={() =>
                    setTxPage((p) => Math.min(txPageCount - 1, p + 1))
                  }
                  disabled={txSafePage >= txPageCount - 1}
                  className="min-h-8 gap-1 px-3"
                >
                  {t("clientOrders.next")}
                  <ChevronRight className="h-4 w-4 rtl:rotate-180" />
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

/* ── Payouts ────────────────────────────────────────────────── */

function DriverPayoutsSection({
  driver,
  canManage,
}: {
  driver: PartnerDriverRow;
  canManage: boolean;
}) {
  const { t, locale } = useI18n();
  const payMutation = useSetPartnerDriverTripFee();
  const profileQuery = usePartnerProfile();
  const defaultRate = profileQuery.data?.driver_trip_fee_default ?? null;
  const [payAmount, setPayAmount] = useState(
    driver.trip_fee_amount != null ? String(driver.trip_fee_amount) : ""
  );

  useEffect(() => {
    setPayAmount(
      driver.trip_fee_amount != null ? String(driver.trip_fee_amount) : ""
    );
  }, [driver.id, driver.trip_fee_amount]);

  const payParsed = payAmount.trim() === "" ? null : Number(payAmount);
  const payValid =
    payParsed == null || (Number.isFinite(payParsed) && payParsed >= 0);
  const payDirty = payValid && payParsed !== (driver.trip_fee_amount ?? null);
  const money = (v: number) => formatCurrency(v, "IQD", locale);

  const handlePaySave = async () => {
    if (!payValid) {
      toast.error(t("errors.invalidTripFee"));
      return;
    }
    try {
      await payMutation.mutateAsync({ driverId: driver.id, amount: payParsed });
      toast.success(t("drivers.tripPaySaved"));
    } catch (err) {
      toast.error(t(mapDriversRpcError(err)));
    }
  };

  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0">
        <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
          {t("drivers.tripPayTitle")}
        </h3>
        <p className="mt-1 text-xs text-[var(--text-muted)]">
          {defaultRate != null && defaultRate > 0
            ? t("drivers.tripPayUsesDefault", { amount: money(defaultRate) })
            : t("drivers.tripPayHint")}
        </p>
      </div>
      <div className="flex flex-none items-center gap-2">
        <input
          className="input-base w-40"
          type="number"
          min="0"
          step="any"
          inputMode="numeric"
          value={payAmount}
          onChange={(e) => setPayAmount(e.target.value)}
          placeholder={t("drivers.tripPayPlaceholder")}
          aria-label={t("drivers.tripPayLabel")}
          disabled={!canManage}
        />
        {canManage ? (
          <Button
            variant="secondary"
            className="min-h-10 px-4"
            disabled={!payDirty || payMutation.isPending}
            onClick={() => void handlePaySave()}
          >
            {payMutation.isPending ? t("common.loading") : t("common.save")}
          </Button>
        ) : null}
      </div>
    </div>
  );
}

/* ── Page ───────────────────────────────────────────────────── */

export default function DriverViewPage() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  // Wildcard route (/app/*) → useParams has no :id; derive it from the path.
  const id = driverIdFromPath(pathname);
  const driversQuery = usePartnerDriversList();
  const [tab, setTab] = useState<DriverTab>("wallet");
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [releaseOpen, setReleaseOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [credentials, setCredentials] = useState<AccountCredentials | null>(null);

  const role = usePartnerSessionStore((s) => s.session?.role ?? "viewer");
  const canManage = role === "owner" || role === "ops";
  const deactivateMutation = useDeactivatePartnerDriver();
  const activateMutation = useActivatePartnerDriver();
  const releaseMutation = useReleasePartnerDriver();
  const resetMutation = useResetPartnerDriverPassword();

  const driver = useMemo(
    () => (driversQuery.data ?? []).find((d) => d.id === id) ?? null,
    [driversQuery.data, id]
  );

  const tabs = [
    { key: "wallet" as const, label: t("drivers.tabs.wallet"), icon: Wallet },
    { key: "settings" as const, label: t("drivers.tabs.settings"), icon: Settings2 },
    { key: "reports" as const, label: t("drivers.tabs.reports"), icon: BarChart3 },
    { key: "operations" as const, label: t("drivers.tabs.operations"), icon: SlidersHorizontal },
  ];

  if (!driver && !driversQuery.isLoading) {
    return (
      <div className="menu-page">
        <section className="menu-panel">
          <div className="menu-panel__body flex flex-col items-center gap-2 py-10 text-center">
            <p className="text-sm font-semibold text-[var(--text-secondary)]">
              {t("drivers.notFound")}
            </p>
            <Button
              variant="secondary"
              onClick={() => navigate(ROUTES.DRIVERS)}
              className="min-h-10 gap-2 px-4"
            >
              <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
              {t("drivers.back")}
            </Button>
          </div>
        </section>
      </div>
    );
  }

  if (!driver) {
    return (
      <div className="menu-page">
        <div className="h-8 w-56 rounded-lg skeleton" />
        <div className="h-40 rounded-xl skeleton" />
      </div>
    );
  }

  const presence = partnerDriverPresence(driver);

  const handleDeactivate = async () => {
    try {
      await deactivateMutation.mutateAsync(driver.id);
      toast.success(t("drivers.toast.deactivated"));
      setDeactivateOpen(false);
      navigate(ROUTES.DRIVERS);
    } catch (err) {
      toast.error(t(mapDriversRpcError(err)));
    }
  };

  const handleActivate = async () => {
    try {
      await activateMutation.mutateAsync(driver.id);
      toast.success(t("drivers.toast.activated"));
    } catch (err) {
      toast.error(t(mapDriversRpcError(err)));
    }
  };

  const handleRelease = async () => {
    try {
      await releaseMutation.mutateAsync(driver.id);
      toast.success(t("drivers.release.toast"));
      setReleaseOpen(false);
      navigate(ROUTES.DRIVERS);
    } catch (err) {
      toast.error(t(mapDriversRpcError(err)));
    }
  };

  const handleResetPassword = async (password: string) => {
    try {
      const result = await resetMutation.mutateAsync({
        driverId: driver.id,
        password,
      });
      setResetOpen(false);
      setCredentials(result);
    } catch (err) {
      toast.error(t(mapAccountError(err)));
    }
  };

  return (
    <div className="menu-page">
      <header className="menu-page__header overview-page__header">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate(ROUTES.DRIVERS)}
            className="inline-flex items-center gap-1.5 text-xs font-bold text-[var(--accent)]"
          >
            <ArrowLeft className="h-3.5 w-3.5 rtl:rotate-180" />
            {t("drivers.back")}
          </button>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <h1 className="menu-page__title overview-page__title">{driver.name}</h1>
            {driver.is_active === false ? (
              <Pill variant="danger">{t("drivers.inactive")}</Pill>
            ) : presence === "offline" ? (
              <span className="drv-presence">
                {t("drivers.presence.offline")}
              </span>
            ) : (
              <Pill variant={presence === "online" ? "success" : "warning"}>
                {t(driverPresenceTranslationKey(presence))}
              </Pill>
            )}
            {driver.driver_mode ? (
              <Pill variant="info">{t(driverModeLabelKey(driver.driver_mode))}</Pill>
            ) : null}
          </div>
          <p className="menu-page__subtitle overview-page__subtitle">
            {driver.phone ? <span dir="ltr">{driver.phone}</span> : t("common.dash")}
            {" · "}
            {vehicleLabel(driver)}
          </p>
        </div>
        <div className="menu-page__header-actions overview-page__header-actions">
          {canManage ? (
            <>
              <Button
                variant="secondary"
                onClick={() => setResetOpen(true)}
                className="min-h-10 gap-2 px-4"
              >
                <KeyRound className="h-4 w-4" />
                {t("accounts.resetAction")}
              </Button>
              {driver.is_active === false ? (
                <Button
                  variant="primary"
                  onClick={() => void handleActivate()}
                  className="min-h-10 gap-2 px-4"
                  disabled={activateMutation.isPending}
                >
                  {activateMutation.isPending
                    ? t("common.loading")
                    : t("drivers.activate")}
                </Button>
              ) : (
                <Button
                  variant="secondary"
                  onClick={() => setDeactivateOpen(true)}
                  className="min-h-10 gap-2 px-4 text-[var(--danger)]"
                  disabled={deactivateMutation.isPending}
                >
                  {t("drivers.deactivate")}
                </Button>
              )}
            </>
          ) : null}
        </div>
      </header>

      <SegmentStrip
        value={tab}
        onChange={setTab}
        options={tabs}
        ariaLabel={t("drivers.tabsAria")}
      />

      {tab === "settings" ? (
        <DriverSettingsSection driver={driver} canManage={canManage} />
      ) : null}
      {tab === "reports" ? <DriverReportsSection driver={driver} /> : null}
      {tab === "wallet" ? (
        <DriverWalletSection driver={driver} canManage={canManage} />
      ) : null}
      {tab === "operations" ? (
        <section className="menu-panel">
          <div className="menu-panel__body space-y-5">
            <DriverPayoutsSection driver={driver} canManage={canManage} />
            <div className="border-t border-[var(--border-subtle)]" />
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <h3 className="text-sm font-extrabold uppercase tracking-wide text-[var(--text-muted)]">
                  {t("drivers.release.title")}
                </h3>
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  {t("drivers.release.hint")}
                </p>
              </div>
              {canManage ? (
                <Button
                  variant="secondary"
                  className="min-h-10 gap-2 px-4 text-[var(--danger)]"
                  disabled={releaseMutation.isPending}
                  onClick={() => setReleaseOpen(true)}
                >
                  {t("drivers.release.cta")}
                </Button>
              ) : null}
            </div>
          </div>
        </section>
      ) : null}

      <Modal
        open={releaseOpen}
        onClose={() => {
          if (!releaseMutation.isPending) setReleaseOpen(false);
        }}
        panelClassName="max-w-md"
      >
        <div className="px-5 py-4">
          <h2 className="text-lg font-extrabold text-[var(--text-primary)]">
            {t("drivers.release.title")}
          </h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            {t("drivers.release.hint")}
          </p>
          <p className="mt-2 text-sm font-bold text-[var(--text-primary)]">
            {driver.name}
          </p>
          <div className="mt-4 flex gap-2">
            <Button
              variant="secondary"
              className="min-h-11 flex-1"
              onClick={() => setReleaseOpen(false)}
              disabled={releaseMutation.isPending}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="secondary"
              className="min-h-11 flex-1 border-[var(--danger)] text-[var(--danger)]"
              onClick={() => void handleRelease()}
              disabled={releaseMutation.isPending}
            >
              {releaseMutation.isPending ? t("common.loading") : t("drivers.release.cta")}
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={deactivateOpen}
        onClose={() => {
          if (!deactivateMutation.isPending) setDeactivateOpen(false);
        }}
        panelClassName="max-w-md"
      >
        <div className="px-5 py-4">
          <h2 className="text-lg font-extrabold text-[var(--text-primary)]">
            {t("drivers.deactivateTitle")}
          </h2>
          <p className="mt-2 text-sm text-[var(--text-secondary)]">
            {t("drivers.deactivateBody")}
          </p>
          <p className="mt-2 text-sm font-bold text-[var(--text-primary)]">
            {driver.name}
          </p>
          <div className="mt-4 flex gap-2">
            <button
              type="button"
              className="btn-secondary min-h-[44px] flex-1"
              onClick={() => setDeactivateOpen(false)}
              disabled={deactivateMutation.isPending}
            >
              {t("common.cancel")}
            </button>
            <button
              type="button"
              className="btn-danger min-h-[44px] flex-1"
              onClick={() => void handleDeactivate()}
              disabled={deactivateMutation.isPending}
            >
              {deactivateMutation.isPending
                ? t("common.loading")
                : t("drivers.deactivate")}
            </button>
          </div>
        </div>
      </Modal>

      <ResetPasswordModal
        open={resetOpen}
        busy={resetMutation.isPending}
        targetLabel={driver.name}
        username={driver.username}
        onClose={() => setResetOpen(false)}
        onSubmit={handleResetPassword}
      />

      <CredentialsModal
        open={!!credentials}
        title={driver.name}
        subtitle={t("accounts.resetHint")}
        credentials={credentials}
        onClose={() => setCredentials(null)}
      />
    </div>
  );
}
